import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  punchApi, type PunchList, type PunchListResponse, type PunchPriority, type PunchSettingsPatch, type PunchTrade,
} from '@/lib/apiClient';
import { Button, Modal } from '@/components';
import { tradeMapEdits, settingsPatchApplied, tradeEditsApplied } from './punchModel';

/**
 * Punch List ▸ Settings (PM/PE): architect/owner approval, defaults for new
 * items, and the trade → sub map that fills in "Assigned to" when an item is
 * added. Only what changed is sent; the server refuses turning approval off
 * while an item before Verified is with the architect/owner, and says which.
 */
export default function PunchSettingsModal({ projectId, data, onClose, onSaved }: {
  projectId: number;
  data: PunchListResponse;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  // What the dialog opened with. The list can refetch while it is open (window
  // focus); comparing against the live list would send back every value
  // another manager changed meanwhile as if it were an edit here (Codex on
  // #565). Only fields changed since opening are saved.
  const [list] = useState(() => data.list!);
  // The scalar settings' baseline: what the dialog opened with, until they
  // save — then what was saved. A retry after the trade map alone failed must
  // not resend them over someone's change made in between (Codex on #565).
  // The map keeps the opening baseline: it has not saved.
  const [scalarBase, setScalarBase] = useState(() => data.list!);
  const subsQ = useQuery({ queryKey: ['punch', projectId, 'subs'], queryFn: () => punchApi.subs(projectId), staleTime: 60_000 });

  const [ao, setAo] = useState(list.requiresAoApproval);
  const [architect, setArchitect] = useState(list.architectName ?? '');
  const [owner, setOwner] = useState(list.ownerRep ?? '');
  const [dueMode, setDueMode] = useState(list.dueMode);
  const [dueDays, setDueDays] = useState(list.dueDays);
  const [dueDate, setDueDate] = useState(list.dueDate ?? '');
  const [priority, setPriority] = useState<PunchPriority>(list.defaultPriority);
  // The map's baseline: what it opened with, less any trade removed here since
  // (the server drops a removed trade's default with it).
  const [initialMap, setInitialMap] = useState<Record<string, string>>(
    () => Object.fromEntries(list.tradeSubs.map((t) => [t.trade, t.subName])),
  );
  const [map, setMap] = useState<Record<string, string>>(initialMap);
  const [newSub, setNewSub] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newTrade, setNewTrade] = useState('');
  const [listBusy, setListBusy] = useState(false);
  const [listNote, setListNote] = useState<string | null>(null);

  const subNames = (subsQ.data?.subs ?? []).map((s) => s.name);
  const handAdded = (subsQ.data?.subs ?? []).filter((s) => s.handAdded ?? s.source === 'manual').map((s) => s.name);

  // The trade and name lists save as they're changed, like adding a sub always
  // has; each call is safe to retry (adding what's there, or removing what's
  // gone, changes nothing). Save Settings is for the fields above them.
  const listChange = async (run: () => Promise<void>, failed: string) => {
    setListBusy(true);
    setError(null);
    setListNote(null);
    try {
      await run();
      void qc.invalidateQueries({ queryKey: ['projects', 'activity', projectId] });
    } catch (e) {
      setError((e as Error)?.message ?? failed);
    } finally {
      setListBusy(false);
    }
  };
  const tradesChanged = (trades: PunchTrade[]) =>
    qc.setQueryData<PunchListResponse>(['punch', projectId], (old) => (old ? { ...old, trades } : old));

  const addSub = () => listChange(async () => {
    const name = newSub.trim();
    if (!name) return;
    qc.setQueryData(['punch', projectId, 'subs'], await punchApi.addSub(projectId, name));
    setNewSub('');
  }, 'Could not add the name.');

  const removeSub = (name: string) => listChange(async () => {
    const r = await punchApi.removeSub(projectId, name);
    qc.setQueryData(['punch', projectId, 'subs'], r);
    const still = r.subs.find((s) => s.name.toLowerCase() === name.toLowerCase());
    if (still?.source === 'commitment') {
      setListNote(`${name} is also a sub in Sage for this job, so it stays on the list.`);
    } else if (still) {
      setListNote(`${name} is still assigned to an item or set as a trade default, so it stays on the list until it isn’t.`);
    }
  }, 'Could not remove the name.');

  const addTrade = () => listChange(async () => {
    const name = newTrade.trim();
    if (!name) return;
    tradesChanged((await punchApi.addTrade(projectId, name)).trades);
    setNewTrade('');
  }, 'Could not add the trade.');

  const removeTrade = (t: PunchTrade) => listChange(async () => {
    tradesChanged((await punchApi.removeTrade(projectId, t.key)).trades);
    // Its default went with it on the server. Drop it here too — from the
    // form, its baseline and the cached list — or a trade re-added under the
    // same name would show the old default while the server assigns nobody
    // (Codex on #566).
    const drop = (m: Record<string, string>) => { const { [t.key]: _gone, ...rest } = m; return rest; };
    setMap(drop);
    setInitialMap(drop);
    qc.setQueryData<PunchListResponse>(['punch', projectId], (old) => (old?.list
      ? { ...old, list: { ...old.list, tradeSubs: old.list.tradeSubs.filter((e) => e.trade !== t.key) } }
      : old));
    // Not exact: the assignee list too. The removed default may have been a
    // name's last use, and Add Item would still offer it (Codex on #566).
    void qc.invalidateQueries({ queryKey: ['punch', projectId] });
  }, 'Could not remove the trade.');

  const setHidden = (t: PunchTrade, hidden: boolean) => listChange(async () => {
    tradesChanged((await punchApi.setTradeHidden(projectId, t.key, hidden)).trades);
  }, hidden ? 'Could not hide the trade.' : 'Could not show the trade.');

  const save = async () => {
    setBusy(true);
    setError(null);
    let savedSome = false;
    try {
      const base = scalarBase;
      const patch: PunchSettingsPatch = {};
      if (ao !== base.requiresAoApproval) patch.requiresAoApproval = ao;
      if ((architect.trim() || null) !== base.architectName) patch.architectName = architect.trim() || null;
      if ((owner.trim() || null) !== base.ownerRep) patch.ownerRep = owner.trim() || null;
      if (dueMode !== base.dueMode) patch.dueMode = dueMode;
      if (dueDays !== base.dueDays) patch.dueDays = dueDays;
      if ((dueDate || null) !== base.dueDate) patch.dueDate = dueDate || null;
      if (priority !== base.defaultPriority) patch.defaultPriority = priority;
      if (Object.keys(patch).length > 0) {
        let saved: PunchList;
        try {
          saved = await punchApi.updateSettings(projectId, patch);
        } catch (e) {
          // The save may have landed with only its reply lost. If the list
          // already holds exactly what was sent, it did: take that as the new
          // baseline, so a retry doesn't resend these fields over a change
          // someone makes afterwards (Codex on #565). Otherwise it failed.
          const now = (await punchApi.get(projectId).catch(() => null))?.list;
          if (!now || !settingsPatchApplied(patch, now as unknown as Record<string, unknown>)) throw e;
          saved = now;
        }
        setScalarBase(saved);
        savedSome = true;
      }

      // Only the trades changed here; the server merges them into the map as
      // it is, under the list lock, so another manager's change to a
      // different trade — even at the same moment — is kept (Codex on #565).
      const changes = tradeMapEdits(initialMap, map);
      if (changes) {
        try {
          await punchApi.setTradeSubs(projectId, changes);
        } catch (e) {
          // Landed with only the reply lost? If the map already holds every
          // edit sent, it did — finish as saved rather than let a retry resend
          // them over a later change (Codex on #565).
          const now = (await punchApi.get(projectId).catch(() => null))?.list;
          if (!now || !tradeEditsApplied(changes, now.tradeSubs)) throw e;
        }
      }

      onSaved();
      onClose();
    } catch (e) {
      // If the settings saved but the trade map didn't, refresh the tab so it
      // shows what IS saved, and say which half failed.
      if (savedSome) onSaved();
      setError(savedSome
        ? `Settings saved, but the trade → sub map was not: ${(e as Error)?.message ?? 'try again'}`
        : (e as Error)?.message ?? 'Settings were not saved.');
    } finally {
      setBusy(false);
    }
  };

  const flow = ['E&W creates', 'Sub fixes', 'E&W verifies', ...(ao ? ['Arch/Owner approves'] : []), 'Closed'];

  return (
    <Modal
      isOpen onClose={onClose} title="Punch list settings" maxWidth={860} closeOnBackdropClick={!busy}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} isLoading={busy}>Save Settings</Button>
        </>
      )}
    >
      <div className="pl-form">
        <section className="pl-form">
          <div className="pl-caption">Architect/Owner approval</div>
          <label className="pl-switch">
            <input type="checkbox" checked={ao} onChange={(e) => setAo(e.target.checked)} />
            The contract requires the architect or owner to sign off each item
          </label>
          <p className="pl-muted" style={{ margin: 0 }}>
            They don’t log in; the PM or PE records their approval.
          </p>
          <div className="pl-chips" aria-label="How an item moves">
            {flow.map((f, i) => (
              <span key={f} className="pl-muted">{i > 0 ? '→ ' : ''}<span className="pl-bic assignee">{f}</span></span>
            ))}
          </div>
          {ao && (
            <div className="pl-grid2">
              <label className="pl-field"><span>Architect</span>
                <input value={architect} maxLength={200} onChange={(e) => setArchitect(e.target.value)} />
              </label>
              <label className="pl-field"><span>Owner rep</span>
                <input value={owner} maxLength={200} onChange={(e) => setOwner(e.target.value)} />
              </label>
            </div>
          )}
        </section>

        <section className="pl-form">
          <div className="pl-caption">Defaults for new items</div>
          <label className="pl-switch">
            <input type="radio" name="pl-due" checked={dueMode === 'offset'} onChange={() => setDueMode('offset')} />
            Due
            <input className="pl-input" type="number" min={0} max={365} style={{ width: 80 }} value={dueDays}
              disabled={dueMode !== 'offset'} onChange={(e) => setDueDays(Math.max(0, Math.min(365, Number(e.target.value) || 0)))} />
            days after the item is added
          </label>
          <label className="pl-switch">
            <input type="radio" name="pl-due" checked={dueMode === 'fixed'} onChange={() => setDueMode('fixed')} />
            One date for the whole list
            <input className="pl-input" type="date" value={dueDate} disabled={dueMode !== 'fixed'}
              onChange={(e) => setDueDate(e.target.value)} />
          </label>
          <div className="pl-row">
            <span className="pl-muted">Default priority</span>
            <div className="pl-chips" role="group" aria-label="Default priority">
              {(['low', 'normal', 'high'] as const).map((p) => (
                <button key={p} type="button" className="pl-chip" aria-pressed={priority === p} onClick={() => setPriority(p)}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="pl-form">
          <div className="pl-caption">Trades on this job</div>
          <p className="pl-muted" style={{ margin: 0 }}>
            The sub picked here is filled in when an item for that trade is added. It can always be changed on the item.
            Hide a standard trade this job doesn’t use, or add the job’s own. Items keep their trade either way.
          </p>
          <div className="pl-grid2">
            {data.trades.map((t) => (
              <div key={t.key} className={`pl-field pl-trade${t.hidden ? ' hidden' : ''}`}>
                <span>
                  {t.label}
                  {t.custom && <span className="pl-tag">This job</span>}
                  {t.hidden && <span className="pl-tag">Hidden</span>}
                </span>
                <div className="pl-row" style={{ flexWrap: 'nowrap' }}>
                  <select aria-label={`${t.label} default sub`} value={map[t.key] ?? ''} disabled={t.hidden}
                    onChange={(e) => setMap((m) => ({ ...m, [t.key]: e.target.value }))}>
                    <option value="">No default</option>
                    {withCurrent(subNames, map[t.key]).map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  {t.custom ? (
                    <Button variant="secondary" size="sm" disabled={listBusy} aria-label={`Remove ${t.label}`}
                      onClick={() => removeTrade(t)}>Remove</Button>
                  ) : (
                    <Button variant="secondary" size="sm" disabled={listBusy} aria-label={`${t.hidden ? 'Show' : 'Hide'} ${t.label}`}
                      onClick={() => setHidden(t, !t.hidden)}>{t.hidden ? 'Show' : 'Hide'}</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="pl-row">
            <input className="pl-input" style={{ flex: '1 1 260px' }} aria-label="New trade" placeholder="Add a trade, e.g. Pipe insulation" value={newTrade} maxLength={40}
              onChange={(e) => setNewTrade(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void addTrade(); }} />
            <Button variant="secondary" size="sm" onClick={addTrade} disabled={!newTrade.trim() || listBusy}>Add Trade</Button>
          </div>
        </section>

        <section className="pl-form">
          <div className="pl-caption">Assigned to — added by hand</div>
          <p className="pl-muted" style={{ margin: 0 }}>
            Subs from Sage are listed already. Add anyone else who takes items: a sub not in Sage, one of our crews, or a person.
          </p>
          {handAdded.length > 0 && (
            <div className="pl-chips" aria-label="Names added by hand">
              {handAdded.map((n) => (
                <span key={n} className="pl-chip pl-name">
                  {n}
                  <button type="button" className="pl-x" aria-label={`Remove ${n}`} disabled={listBusy}
                    onClick={() => removeSub(n)}>×</button>
                </span>
              ))}
            </div>
          )}
          <div className="pl-row">
            <input className="pl-input" style={{ flex: '1 1 260px' }} aria-label="New name" placeholder="e.g. Pipe crew 2, or a person’s name" value={newSub} maxLength={200}
              onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void addSub(); }} />
            <Button variant="secondary" size="sm" onClick={addSub} disabled={!newSub.trim() || listBusy}>Add Name</Button>
          </div>
          {listNote && <div className="pl-note">{listNote}</div>}
        </section>

        {error && <div className="pl-error">{error}</div>}
      </div>
    </Modal>
  );
}

/** Keep a currently mapped name selectable even if it has dropped off the list. */
function withCurrent(names: string[], current: string | undefined): string[] {
  if (current && !names.some((n) => n.toLowerCase() === current.toLowerCase())) return [current, ...names];
  return names;
}
